/**
 * 12 chromatic groups covering the full spectrum, with 8 carefully calibrated
 * high-saturation, high-visibility vibrant shades each.
 */
const CHROMATIC_GROUPS: readonly (readonly string[])[] = [
  // 1. Ruby Reds & Crimsons
  ['#FF1744', '#FF2A2A', '#E60049', '#D81B60', '#FF3366', '#FF5252', '#C2185B', '#FF003C'],
  // 2. Flame Oranges & Vermilions
  ['#FF5722', '#FF6E00', '#FF8C00', '#FF7043', '#F4511E', '#E65100', '#FF9100', '#FFA726'],
  // 3. Warm Ambers & Marigolds
  ['#FF9F1A', '#FFB300', '#FFA000', '#FFC107', '#FBC02D', '#F57F17', '#FFB800', '#FFAB00'],
  // 4. Electric Yellows & Sunshines
  ['#FFD600', '#FFE600', '#FFEA00', '#FFF000', '#F9CA24', '#ECCC68', '#F6E58D', '#FFE066'],
  // 5. Chartreuses & Electric Limes
  ['#76FF03', '#AEEA00', '#BFFF00', '#64DD17', '#C6FF00', '#8EE000', '#99FF00', '#CCFF00'],
  // 6. Bright Greens & Spring Mints
  ['#00E640', '#00C853', '#00E676', '#2ED573', '#10AC84', '#05C46B', '#26DE81', '#00BFA5'],
  // 7. Emeralds & Teals
  ['#00B894', '#00CEC9', '#1DD1A1', '#10AC84', '#01A3A4', '#009688', '#00897B', '#00796B'],
  // 8. Electric Cyans & Aquas
  ['#00F3FF', '#00E5FF', '#00D2D3', '#18DCFF', '#0ABDE3', '#48DBFB', '#1DE9B6', '#00FFFF'],
  // 9. Sky & Azure Blues
  ['#00B0FF', '#0091EA', '#0984E3', '#2E86DE', '#3867D6', '#1B9CFC', '#45AAF2', '#2BCBBA'],
  // 10. Royal & Indigo Blues
  ['#2979FF', '#3D5AFE', '#5352ED', '#3742FA', '#4834D4', '#5F27CD', '#341F97', '#686DE0'],
  // 11. Violets & Deep Purples
  ['#8A2BE2', '#9C27B0', '#AA00FF', '#D500F9', '#8854D0', '#A55EEA', '#7D5FFF', '#6C5CE7'],
  // 12. Magentas & Vivid Pinks
  ['#FF00A0', '#FF1493', '#FF4081', '#FD79A8', '#E056FD', '#BE2EDD', '#F368E0', '#FF6B81']
];

/**
 * 96 distinct, vibrant colors interleaved across 12 chromatic groups.
 * Interleaving guarantees that consecutive index steps traverse dramatically
 * different color families (e.g. Red -> Cyan -> Lime -> Purple...).
 */
export const VIBRANT_COLOR_PALETTE: readonly string[] = (() => {
  const result: string[] = [];
  const shadesPerGroup = CHROMATIC_GROUPS[0].length;
  for (let s = 0; s < shadesPerGroup; s++) {
    for (let g = 0; g < CHROMATIC_GROUPS.length; g++) {
      result.push(CHROMATIC_GROUPS[g][s]);
    }
  }
  return result;
})();

/**
 * 32-bit MurmurHash3 algorithm for high-entropy avalanche dispersion.
 *
 * @param str - Input string to hash
 * @returns 32-bit unsigned integer
 */
function murmur3_32(str: string): number {
  let h = 0x9747b28c;
  for (let i = 0; i < str.length; i++) {
    let k = str.charCodeAt(i);
    k = Math.imul(k, 0xcc9e2d51);
    k = (k << 15) | (k >>> 17);
    k = Math.imul(k, 0x1b873593);
    h ^= k;
    h = (h << 13) | (h >>> 19);
    h = (Math.imul(h, 5) + 0xe6546b64) >>> 0;
  }
  h ^= str.length;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/**
 * Generates a deterministic, vibrant color from a node's identifier or short name.
 *
 * Why: Meshtastic coverage maps require immediate visual distinction between transmitting
 * nodes. Mapping an avalanche-dispersed 32-bit hash across an interleaved 96-color palette
 * guarantees that even nodes with similar names or sequential IDs receive distinctly separated,
 * vivid colors spanning the entire spectrum without clustering in a single chromatic band.
 *
 * @param nodeIdentifier - Unsigned 32-bit node number or string identifier
 * @param shortName - Optional fallback string for hashing if nodeIdentifier is numeric
 * @returns Vibrant 6-digit hex color string from the palette (e.g. '#FF6E00')
 */
export function getNodeColor(nodeIdentifier: number | string, shortName?: string): string {
  const seedStr = typeof nodeIdentifier === 'string'
    ? nodeIdentifier
    : shortName && shortName.length > 0
      ? shortName
      : (nodeIdentifier >>> 0).toString(16);

  const hash = murmur3_32(seedStr);
  return VIBRANT_COLOR_PALETTE[hash % VIBRANT_COLOR_PALETTE.length];
}

/**
 * Determines whether black ('#000000') or white ('#ffffff') text provides
 * optimal legibility against a given background color using standard YIQ perceived brightness.
 *
 * Why: Colored node nameplates require automatic high-contrast typography
 * so that bright backgrounds (e.g. neon yellow, lime, cyan) receive crisp black text,
 * while deeper backgrounds (e.g. purple, blue, crimson) receive clean white text.
 *
 * @param hexColor - 6 or 7 character hex color string (e.g. '#FFD600')
 * @returns '#000000' or '#ffffff'
 */
export function getContrastTextColor(hexColor: string): '#000000' | '#ffffff' {
  const clean = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;
  const r = parseInt(clean.substring(0, 2), 16) || 0;
  const g = parseInt(clean.substring(2, 4), 16) || 0;
  const b = parseInt(clean.substring(4, 6), 16) || 0;

  // Standard YIQ perceived brightness (ITU-R BT.601)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 135 ? '#000000' : '#ffffff';
}

/**
 * Converts a raw LoRa Signal-to-Noise Ratio (SNR) value into a normalized opacity value [0.18..0.90].
 *
 * Why: Signal strength should be visually represented through opacity (weak signals appear
 * faint/ghostly, strong signals appear solid/opaque). Clamping to [0.18..0.90] guarantees
 * that critical edge-of-reception signals remain faintly noticeable without disappearing,
 * while excellent signals never completely occlude underlying map vector lines.
 *
 * @param snr - LoRa SNR value in dB (typically ranging between -20 dB and +10 dB)
 * @returns Opacity float clamped between 0.18 and 0.90
 */
export function snrToOpacity(snr: number): number {
  const minSnr = -18;
  const maxSnr = 8;
  const minOpacity = 0.18;
  const maxOpacity = 0.90;

  if (snr <= minSnr) return minOpacity;
  if (snr >= maxSnr) return maxOpacity;

  const ratio = (snr - minSnr) / (maxSnr - minSnr);
  return Number((minOpacity + ratio * (maxOpacity - minOpacity)).toFixed(3));
}

/**
 * Combines an RGB hex color with an alpha float into an rgba() CSS color string.
 *
 * Why: Leaflet canvas and SVG circle markers require explicit CSS rgba() strings for
 * fill styling with dynamic per-packet alpha channels.
 *
 * @param hex - 7-character hex color string (#rrggbb)
 * @param alpha - Opacity value between 0.0 and 1.0
 * @returns Valid CSS rgba string (e.g. 'rgba(0, 243, 255, 0.45)')
 */
export function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.startsWith('#') ? hex.slice(1) : hex;
  const r = parseInt(clean.substring(0, 2), 16) || 0;
  const g = parseInt(clean.substring(2, 4), 16) || 0;
  const b = parseInt(clean.substring(4, 6), 16) || 0;
  const clampedAlpha = Math.max(0, Math.min(1, alpha));
  return `rgba(${r}, ${g}, ${b}, ${clampedAlpha.toFixed(3)})`;
}

/**
 * Node color contribution entry for multi-node hexagonal cell blending.
 */
export interface NodeColorContribution {
  color: string;
  snr: number;
}

/**
 * Maps a LoRa Signal-to-Noise Ratio (SNR) value into a strictly positive relative mixing weight [0.05..1.0].
 *
 * Why: Meshtastic signals range from extreme edge-of-detection (-20 dB) to clean line-of-sight (+10 dB).
 * In hexagonal cells overlapping multiple transmitting nodes, each node's color contribution must scale
 * proportionally with RF signal strength so dominant transmitters stand out clearly, while weaker nodes
 * contribute subtle chromatic tints without dropping below perceptual thresholds.
 *
 * @param snr - LoRa SNR in dB
 * @returns Normalized weight float between 0.05 and 1.0
 */
export function snrToWeight(snr: number): number {
  const minSnr = -20;
  const maxSnr = 10;
  const minWeight = 0.05;
  const maxWeight = 1.0;

  if (snr <= minSnr) return minWeight;
  if (snr >= maxSnr) return maxWeight;

  const ratio = (snr - minSnr) / (maxSnr - minSnr);
  return Number((minWeight + ratio * (maxWeight - minWeight)).toFixed(4));
}

/**
 * Blends multiple node colors into a single composite hex color using SNR-weighted quadratic luminance averaging.
 *
 * Why: Simple linear RGB averaging causes complementary or distant palette colors (e.g. red and green)
 * to lose luminance and turn into muddy, dark olive-brown hues. Using quadratic component blending
 * ($\sqrt{\sum w \cdot c^2 / \sum w}$) preserves perceived luminous intensity and chroma vibrancy across
 * the full 96-color palette when multiple transmitters overlap within the same spatial sector.
 *
 * @param contributions - Array of node color contributions with respective SNR values
 * @returns 7-character hex color string (#rrggbb)
 */
export function blendNodeColors(contributions: NodeColorContribution[]): string {
  if (contributions.length === 0) {
    return '#00f3ff';
  }
  if (contributions.length === 1) {
    return contributions[0].color;
  }

  let totalWeight = 0;
  let rSqSum = 0;
  let gSqSum = 0;
  let bSqSum = 0;

  for (const item of contributions) {
    const w = snrToWeight(item.snr);
    const clean = item.color.startsWith('#') ? item.color.slice(1) : item.color;
    const r = parseInt(clean.substring(0, 2), 16) || 0;
    const g = parseInt(clean.substring(2, 4), 16) || 0;
    const b = parseInt(clean.substring(4, 6), 16) || 0;

    totalWeight += w;
    rSqSum += w * r * r;
    gSqSum += w * g * g;
    bSqSum += w * b * b;
  }

  if (totalWeight <= 0) {
    return contributions[0].color;
  }

  const rFinal = Math.min(255, Math.max(0, Math.round(Math.sqrt(rSqSum / totalWeight))));
  const gFinal = Math.min(255, Math.max(0, Math.round(Math.sqrt(gSqSum / totalWeight))));
  const bFinal = Math.min(255, Math.max(0, Math.round(Math.sqrt(bSqSum / totalWeight))));

  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  return `#${toHex(rFinal)}${toHex(gFinal)}${toHex(bFinal)}`;
}
